import { SectionTitle } from '@/components/SectionTitle';
import Typography from '@/components/Typography';
import { TextStyles, theme } from '@/constants';
import styled from 'styled-components';

/** The carrier header tile is rounded noticeably more than the card-grid tiles. */
export const CARRIER_LOGO_RADIUS = theme.spacing[8];

export const CarrierHeader = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[16],
}));

export const CarrierName = styled(Typography).attrs({
  variant: TextStyles['Large Text Display'],
  type: 'h3',
})(({ theme }) => ({
  margin: 0,
  color: theme.colors.darkBlue,
}));

export const Introduction = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
  type: 'p',
})(({ theme }) => ({
  margin: `${theme.spacing[24]} 0 0`,
  color: theme.colors.mediumEmphasisBlack,
}));

/** Same treatment as a section title, but the design draws its rule wider than the standard 24px. */
export const OverviewTitle = styled(SectionTitle)(({ theme }) => ({
  marginTop: theme.spacing[8],
  marginBottom: theme.spacing[16],

  [`${theme.mediaBreakpointUp.md}`]: {
    marginBottom: theme.spacing[16],
  },

  '::after': {
    width: theme.spacing[40],
  },
}));

export const DetailGrid = styled.dl(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing[16],
  margin: 0,

  // The design keeps two columns even at 375px rather than stacking — the values are short
  // enough to hold, and stacking would push the block below the fold on a phone.
  [`${theme.mediaBreakpointUp.sm}`]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: theme.spacing[8],
  },
}));

export const DetailLabel = styled(Typography).attrs({
  variant: TextStyles['Overline'],
  type: 'dt',
})(({ theme }) => ({
  margin: 0,
  color: theme.colors.highEmphasisBlack,
}));

/**
 * Plain detail text. Headquarters and phone sit at medium emphasis in the design — the label above
 * them carries the weight, and the figures themselves are reference, not calls to action.
 */
export const DetailText = styled(Typography).attrs({
  variant: TextStyles['Body Text Small'],
  type: 'dd',
})(({ theme }) => ({
  margin: `${theme.spacing[8]} 0 0`,
  color: theme.colors.mediumEmphasisBlack,
  overflowWrap: 'anywhere',
}));

export const DetailLink = styled.a(({ theme }) => ({
  color: theme.colors.hyperlink,
  textDecoration: 'underline',
  overflowWrap: 'anywhere',

  '&:hover': {
    color: theme.colors.blue,
  },
}));
